import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import ArticleCard from "@/components/feature/ArticleCard";
import { categories } from "@/mocks/categories";
import { articles, Article } from "@/mocks/articles";

const HERO_IMG =
  "https://readdy.ai/api/search-image?query=Stylized%20vibrant%20manga%20landscape%20illustration%20of%20a%20young%20reader%20immersed%20in%20floating%20comic%20panels%20while%20riding%20an%20evening%20train%20and%20relaxing%20before%20sleep%2C%20warm%20orange%20and%20coral%20tones%20with%20soft%20cream%20background%2C%20dreamy%20editorial%20anime%20art%2C%20cinematic%20wide%20composition&width=1600&height=900&seq=dm-hero-home&orientation=landscape";

const VALUE_IMG =
  "https://readdy.ai/api/search-image?query=Warm%20stylized%20manga%20illustration%20of%20cozy%20reading%20moments%20across%20daily%20life%2C%20person%20reading%20comics%20on%20a%20commute%2C%20relaxing%20in%20bed%20at%20night%2C%20enjoying%20tea%20on%20a%20sofa%2C%20soft%20cream%20and%20coral%20palette%2C%20gentle%20editorial%20anime%20art%2C%20vertical%20composition&width=1200&height=900&seq=dm-about-illus&orientation=portrait";

export default function Home() {
  useSeo({
    title: "抖漫 - 通勤睡前日常碎片时间随时看的漫画速看平台",
    description:
      "抖漫专注碎片化观看场景，提供抖漫速看、抖漫精选、抖漫片段内容。通勤路上、睡前放松或日常休息，打开抖漫即可快速进入漫画世界。",
    keywords: "抖漫,抖漫速看,抖漫片段,抖漫精选,抖漫日常,抖漫通勤,抖漫睡前",
    path: "/",
    type: "website",
  });

  const byCategory = (slug: string) => articles.filter((a) => a.category === slug);
  const featured = articles.filter((a) => a.featured);
  const sukan = byCategory("sukan");
  const jingxuan = byCategory("jingxuan");
  const pianduan = byCategory("pianduan");
  const tongqin = byCategory("tongqin");
  const shuiqian = byCategory("shuiqian");

  const faq = [
    {
      q: "抖漫是什么平台？",
      a: "抖漫是专注碎片化观看场景的漫画内容平台，提供抖漫速看、抖漫精选、抖漫片段等内容，适配通勤、睡前和日常休息等碎片时间。",
    },
    {
      q: "抖漫速看和抖漫片段有什么区别？",
      a: "抖漫速看是三五分钟讲完一部作品的核心剧情与看点，抖漫片段则是把一部作品中最精彩的名场面单独剪辑展示，两者都能在短时间内带来完整体验。",
    },
    {
      q: "抖漫内容免费吗？",
      a: "抖漫大部分速看与片段内容均可免费浏览，部分精选长篇会提供会员优先体验，具体以页面展示为准。",
    },
  ];

  return (
    <>
      {/* Hero */}
      <section className="relative h-[520px] md:h-[640px] w-full overflow-hidden">
        <img
          src={HERO_IMG}
          alt="抖漫漫画速看平台"
          title="抖漫 - 碎片时间随时看的漫画速看平台"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
        <div className="relative z-10 mx-auto flex h-full w-full max-w-7xl flex-col items-center justify-center px-4 text-center">
          <span className="rounded-full bg-background-50/15 px-4 py-1.5 text-xs md:text-sm text-background-50 backdrop-blur">
            通勤 · 睡前 · 日常 碎片时间随时看
          </span>
          <h1 className="mt-5 font-heading text-4xl md:text-6xl text-background-50 leading-tight">
            抖漫<span className="text-accent-400">速看</span>，一部好漫画
          </h1>
          <p className="mt-5 max-w-2xl text-sm md:text-lg leading-7 text-background-100/90">
            抖漫专注碎片化观看场景，提供抖漫速看、抖漫精选、抖漫片段内容。通勤路上、睡前放松或日常休息，打开抖漫即可快速进入漫画世界，精选内容适配碎片时间。
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center gap-3">
            <Link
              to="/category/sukan"
              className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-7 py-3 text-sm font-medium text-background-50 whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              <i className="ri-play-circle-line"></i> 开始速看
            </Link>
            <Link
              to="/category/jingxuan"
              className="inline-flex items-center gap-2 rounded-full bg-background-50/15 px-7 py-3 text-sm font-medium text-background-50 whitespace-nowrap backdrop-blur hover:bg-background-50/25 transition-colors"
            >
              浏览精选 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Category quick nav */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 -mt-12 relative z-20" aria-label="内容分类导航">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
          {categories.map((c) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              className="flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5 text-center transition-all duration-300 hover:-translate-y-1 hover:border-primary-300"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                <i className={`${c.icon} text-xl`}></i>
              </span>
              <div>
                <p className="text-sm font-bold text-foreground-950 whitespace-nowrap">{c.name}</p>
                <p className="mt-1 text-xs text-foreground-500">{c.tagline}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured picks */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 pt-12 md:pt-16">
        <div className="flex items-end justify-between">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
              <i className="ri-fire-line"></i> 编辑推荐
            </span>
            <h2 className="mt-3 font-heading text-2xl md:text-3xl text-foreground-950">本周不容错过</h2>
          </div>
        </div>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {featured.map((a: Article) => (
            <ArticleCard key={a.slug} article={a} />
          ))}
        </div>
      </section>

      {/* Sukan section */}
      <Section
        icon="ri-flashlight-line"
        title="抖漫速看"
        subtitle="一分钟看懂一部好漫画"
        linkLabel="更多速看"
        linkTo="/category/sukan"
      >
        {sukan.slice(0, 3).map((a) => (
          <ArticleCard key={a.slug} article={a} />
        ))}
      </Section>

      {/* Jingxuan section */}
      <Section
        icon="ri-star-line"
        title="抖漫精选"
        subtitle="编辑严选的口碑佳作"
        linkLabel="更多精选"
        linkTo="/category/jingxuan"
      >
        {jingxuan.slice(0, 3).map((a) => (
          <ArticleCard key={a.slug} article={a} />
        ))}
      </Section>

      {/* Pianduan + Tongqin + Shuiqian mixed */}
      <section className="bg-background-100/70 py-12 md:py-16 mt-12">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-6">
            <MiniSection title="抖漫片段" icon="ri-clapperboard-line" linkTo="/category/pianduan" items={pianduan.slice(0, 2)} />
            <MiniSection title="通勤时刻" icon="ri-train-line" linkTo="/category/tongqin" items={tongqin.slice(0, 2)} />
            <MiniSection title="睡前放松" icon="ri-moon-line" linkTo="/category/shuiqian" items={shuiqian.slice(0, 2)} />
          </div>
        </div>
      </section>

      {/* Value props + about intro */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
          <div className="relative aspect-[4/3] w-full overflow-hidden rounded-lg">
            <img src={VALUE_IMG} alt="抖漫碎片化阅读场景" title="抖漫 - 随时随地看漫画" className="h-full w-full object-cover" />
          </div>
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
              <i className="ri-heart-3-line"></i> 为碎片时间而生
            </span>
            <h2 className="mt-3 font-heading text-2xl md:text-3xl text-foreground-950">打开抖漫，随时进入漫画世界</h2>
            <p className="mt-4 text-sm md:text-base leading-7 text-foreground-600">
              我们深知生活节奏越来越快，完整追完一部漫画变得越来越奢侈。抖漫把内容切成一个个恰好能填满碎片时间的片段，让通勤、睡前、午休都成为漫画时间。
            </p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { num: "1000+", label: "精选作品" },
                { num: "60%", label: "用户来自通勤场景" },
                { num: "4.9", label: "编辑评分" },
              ].map((s) => (
                <div key={s.label} className="rounded-lg border border-background-200 bg-background-50 p-4 text-center">
                  <p className="font-heading text-2xl text-primary-600">{s.num}</p>
                  <p className="mt-1 text-xs text-foreground-500">{s.label}</p>
                </div>
              ))}
            </div>
            <Link
              to="/about"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-secondary-500 px-6 py-3 text-sm font-medium text-background-50 whitespace-nowrap hover:bg-secondary-600 transition-colors"
            >
              了解抖漫 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-background-100/70 py-12 md:py-16" aria-label="常见问题">
        <div className="mx-auto max-w-3xl px-4 md:px-6">
          <h2 className="text-center font-heading text-2xl md:text-3xl text-foreground-950">关于抖漫的常见问题</h2>
          <div className="mt-8 space-y-3">
            {faq.map((f) => (
              <details key={f.q} className="group rounded-lg border border-background-200 bg-background-50 p-5">
                <summary className="flex cursor-pointer items-center justify-between gap-4 text-sm md:text-base font-semibold text-foreground-950 list-none">
                  {f.q}
                  <i className="ri-arrow-down-s-line text-xl text-foreground-400 transition-transform group-open:rotate-180"></i>
                </summary>
                <p className="mt-3 text-sm leading-6 text-foreground-600">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function Section({
  icon,
  title,
  subtitle,
  linkLabel,
  linkTo,
  children,
}: {
  icon: string;
  title: string;
  subtitle: string;
  linkLabel: string;
  linkTo: string;
  children: ReactNode;
}) {
  return (
    <section className="mx-auto max-w-7xl px-4 md:px-6 pt-12 md:pt-16">
      <div className="flex items-end justify-between gap-4">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
            <i className={icon}></i> {title}
          </span>
          <h2 className="mt-3 font-heading text-2xl md:text-3xl text-foreground-950">{title}</h2>
          <p className="mt-1 text-sm text-foreground-500">{subtitle}</p>
        </div>
        <Link
          to={linkTo}
          className="inline-flex items-center gap-1.5 rounded-full border border-background-200 px-4 py-2 text-sm text-foreground-700 whitespace-nowrap hover:border-primary-300 hover:text-primary-600 transition-colors"
        >
          {linkLabel} <i className="ri-arrow-right-line"></i>
        </Link>
      </div>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">{children}</div>
    </section>
  );
}

function MiniSection({
  title,
  icon,
  linkTo,
  items,
}: {
  title: string;
  icon: string;
  linkTo: string;
  items: Article[];
}) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-heading text-xl md:text-2xl text-foreground-950">
          <i className={`${icon} text-primary-600`}></i> {title}
        </h2>
        <Link to={linkTo} className="text-sm text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap">
          查看全部
        </Link>
      </div>
      <div className="mt-5 space-y-4">
        {items.map((a) => (
          <Link
            key={a.slug}
            to={`/article/${a.slug}`}
            className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary-300"
          >
            <div className="h-20 w-28 shrink-0 overflow-hidden rounded-md bg-background-100">
              <img src={a.cover} alt={a.title} className="h-full w-full object-cover object-top" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
                {a.title}
              </h3>
              <p className="mt-1 text-xs text-foreground-500">{a.readTime} · {a.views}次观看</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}